/**
 * Responsibility: show one price and, inseparably, where it came from.
 *
 * This is the smallest component on the page and the most load-bearing. A
 * sold price and an asking price look identical as numbers and mean entirely
 * different things: one is what someone paid, the other is what a seller
 * hopes for, and on graded singles the gap runs to a third or more. So the
 * basis is rendered next to the figure and there is no variant of this
 * component that omits it.
 *
 * Sample size is in the tooltip rather than the cell. It matters - a median of
 * three is an anecdote - but it matters second, and a table with two numbers
 * per cell is a table nobody reads.
 */
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { EMPTY } from '@kit/utils';
import type { CardPrice } from '../../api/client';
import { money } from './money';

type PriceCellProps = {
  price: CardPrice | null;
  /** Shown when there is no price at all, e.g. "none sold". */
  absent?: string;
};

const BASIS_LABEL: Record<string, string> = {
  SOLD: 'sold',
  LISTED: 'asking',
  MANUAL: 'yours',
};

export function PriceCell({ price, absent = EMPTY }: PriceCellProps) {
  if (!price) {
    return (
      <Typography variant="body2" color="text.disabled">
        {absent}
      </Typography>
    );
  }

  const label = BASIS_LABEL[price.basis] ?? price.basis.toLowerCase();
  const detail = price.is_thin
    ? `Only ${price.sample_size} observation(s) - treat as an anecdote, not a market`
    : `Median of ${price.sample_size} observations${price.source ? ` from ${price.source}` : ''}`;

  return (
    <Stack spacing={0} sx={{ minWidth: 0 }} aria-label={detail}>
      <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
        {money(price.amount)}
      </Typography>
      <Typography
        variant="caption"
        // Asking prices are dimmed and sold prices are not, so the eye
        // learns which rows rest on real transactions without having to
        // read the word every time.
        color={price.basis === 'LISTED' ? 'warning.main' : 'text.secondary'}
      >
        {label}
        {price.is_thin ? ` · ${price.sample_size}` : ''}
      </Typography>
    </Stack>
  );
}
